/* <usdt-price> — the USDT equivalent next to the main price (snippet product-price-usdt, styles in product.css).

   Rate: with data-source="nbu" the official NBU hryvnia/USD rate is loaded from bank.gov.ua (1 USDT = 1 USD,
   optional data-markup in %), cached in localStorage for 3 hours and shared by every <usdt-price> on the page.
   A cached rate up to 3 days old is shown at once and refreshed in the background; only a first visit waits
   for the network (up to 2.5 s, the amount keeps its place hidden meanwhile). If NBU does not answer, the
   amount rendered by Liquid at the manual fallback rate is shown. data-source="manual" keeps that amount as is.

   Rolls the digits into place like a mechanical counter whenever the element enters the DOM: on page load and
   on every variant change, because product.js swaps the whole price section innerHTML and the browser upgrades
   the freshly inserted element again. A click/tap toggles the exchange-rate hint (hover does it on desktop).
   Loaded with defer by sections/t-product and sections/gs-featured-product, next to product.js. */
(() => {
  if (customElements.get('usdt-price')) return;

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  const ROWS = 20; // two 0–9 cycles: every digit can roll forward a few rows and still land in the second cycle

  const NBU_URL = 'https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange?valcode=USD&json';
  const CACHE_KEY = 'uniform:nbu-usd';
  const FRESH_MS = 3 * 60 * 60 * 1000;       // refetch after 3 h — NBU sets the rate once a day
  const STALE_MS = 3 * 24 * 60 * 60 * 1000;  // an older rate is still shown instantly while a fresh one loads
  const TIMEOUT_MS = 2500;

  let memo = null;      // { rate, date, t }
  let inflight = null;  // Promise<memo | null>
  let failed = false;   // NBU did not answer on this page view: don't make every variant change wait again

  function readCache() {
    if (memo) return memo;
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (c && c.rate > 0 && Date.now() - c.t < STALE_MS) memo = c;
    } catch (e) { /* storage blocked or corrupt: behave as a first visit */ }
    return memo;
  }

  function fetchRate() {
    if (inflight) return inflight;
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => ctrl && ctrl.abort(), TIMEOUT_MS);
    inflight = fetch(NBU_URL, { signal: ctrl ? ctrl.signal : undefined, credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`NBU ${r.status}`))))
      .then((list) => {
        const row = Array.isArray(list) ? list.find((x) => x && x.cc === 'USD') : null;
        const rate = row ? parseFloat(row.rate) : NaN;
        if (!(rate > 1 && rate < 1000)) throw new Error('NBU: unexpected rate');
        memo = { rate, date: row.exchangedate || '', t: Date.now() };
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(memo)); } catch (e) { /* private mode */ }
        return memo;
      })
      .catch(() => { failed = true; return null; })
      .finally(() => { clearTimeout(timer); inflight = null; });
    return inflight;
  }

  const thousands = (digits, sep) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

  // Shopify money_format ("₴{{amount}}") → "₴44.86"
  function formatMoney(cents, format) {
    const fmt = (dec, th, ds) => {
      const [int, frac] = (cents / 100).toFixed(dec).split('.');
      return thousands(int, th) + (frac ? ds + frac : '');
    };
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(0, ',', '.');
        case 'amount_with_comma_separator': return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
        case 'amount_with_space_separator': return fmt(2, ' ', ',');
        default: return fmt(2, ',', '.');
      }
    });
  }

  class UsdtPrice extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      this.addEventListener('click', () => this.classList.toggle('is-open'));

      if (this.dataset.source !== 'nbu') { this.reveal(); return; }

      const cached = readCache();
      if (cached) {
        this.apply(cached);
        if (Date.now() - cached.t > FRESH_MS && !failed) fetchRate(); // next page view gets the new rate
        return;
      }
      if (failed) { this.apply(null); return; }
      fetchRate().then((nbu) => this.apply(nbu));
    }

    // nbu = { rate, date } or null (use the Liquid amount at the manual fallback rate)
    apply(nbu) {
      const d = this.dataset;
      const line = this.nextElementSibling && this.nextElementSibling.matches('[data-usdt-rate-line]') ? this.nextElementSibling : null;
      const amount = this.querySelector('[data-usdt-amount]');

      if (nbu) {
        const price = parseInt(d.price, 10); // kopecks
        const rate = nbu.rate * (1 + (parseFloat(d.markup) || 0) / 100);
        if (amount && price > 0 && rate > 0) {
          amount.textContent = thousands(String(Math.round(price / 100 / rate)), '\u202f'); // thin space, as in the ₴ price
          const tpl = (parseFloat(d.markup) || 0) === 0 ? d.hintNbu : d.hint;
          const text = (tpl || '').replace('__RATE__', formatMoney(Math.round(rate * 100), d.moneyFormat));
          const hint = this.querySelector('.usdt-price__hint > span');
          if (hint && text) hint.textContent = text;
          if (line && text) line.textContent = text;
          if (nbu.date) this.dataset.rateDate = nbu.date;
        }
      }

      if (!amount || !amount.textContent.trim()) { // no NBU rate and no manual fallback: nothing honest to show
        this.hidden = true;
        if (line) line.hidden = true;
        return;
      }
      this.classList.remove('is-pending');
      this.reveal();
    }

    reveal() {
      const amount = this.querySelector('[data-usdt-amount]');
      if (amount && !REDUCED.matches) this.roll(amount);
    }

    roll(amount) {
      const text = amount.textContent.trim();
      const frag = document.createDocumentFragment();
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = text; // screen readers get the plain number; the columns below are decoration
      frag.append(sr);

      const tracks = [];
      let i = 0;
      for (const ch of text) {
        const cell = document.createElement('span');
        cell.setAttribute('aria-hidden', 'true');
        if (!/\d/.test(ch)) { cell.textContent = ch; frag.append(cell); continue; }

        cell.className = 'usdt-digit';
        const ph = document.createElement('span');
        ph.className = 'usdt-digit__ph';
        ph.textContent = ch;
        const track = document.createElement('span');
        track.className = 'usdt-digit__track';
        for (let r = 0; r < ROWS; r++) {
          const s = document.createElement('span');
          s.textContent = r % 10;
          track.append(s);
        }
        const d = +ch;
        const distance = Math.min(10, 3 + 2 * i); // right-hand digits travel further, like a real counter
        track.style.transition = 'none';
        track.style.transform = `translateY(${-(10 + d - distance)}em)`;
        track.style.setProperty('--usdt-delay', `${i * 45}ms`);
        tracks.push([track, `translateY(${-(10 + d)}em)`]);
        cell.append(ph, track);
        frag.append(cell);
        i++;
      }

      amount.replaceChildren(frag);
      void amount.offsetWidth; // commit the start positions before the transition kicks in
      requestAnimationFrame(() => tracks.forEach(([track, end]) => { track.style.transition = ''; track.style.transform = end; }));
    }
  }

  customElements.define('usdt-price', UsdtPrice);

  /* <usdt-total data-cents="…"> — "≈ 380 USDT" for the cart drawer total (snippets/header-cart). rivets keeps
     data-cents in sync with cart.total_price; the same NBU rate / cache / fallback as <usdt-price>. */
  class UsdtTotal extends HTMLElement {
    static get observedAttributes() { return ['data-cents']; }
    connectedCallback() { this.render(); }
    attributeChangedCallback() { this.render(); }
    rate() {
      const d = this.dataset;
      const fallback = parseFloat(d.fallbackRate) || 0;
      if (d.source !== 'nbu') return fallback;
      const nbu = readCache();
      if (nbu) {
        if (Date.now() - nbu.t > FRESH_MS && !failed) fetchRate();
        return nbu.rate * (1 + (parseFloat(d.markup) || 0) / 100);
      }
      if (!failed && !this._waiting) { this._waiting = true; fetchRate().then(() => { this._waiting = false; this.render(); }); }
      return failed ? fallback : 0;
    }
    render() {
      const cents = parseInt(this.dataset.cents, 10) || 0;
      const rate = this.rate();
      if (!(cents > 0) || !(rate > 0)) { this.textContent = ''; return; }
      this.textContent = `≈ ${thousands(String(Math.round(cents / 100 / rate)), '\u202f')} ${this.dataset.label || 'USDT'}`;
    }
  }
  customElements.define('usdt-total', UsdtTotal);
})();
